import { StyledCreateCategoryModal } from "./styledComponents";
import { Button, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { CreateCategoryForm } from "./create-category-form/CreateCategoryForm";
import { CreateCategoryModalContentsProps } from "./types";

export interface CreateCategoryModalProps extends CreateCategoryModalContentsProps {
    /**
     * is the modal currently open
     */
    open: boolean;

    /**
     * cancel button callback
     */
    onCancel: () => void;

    /**
     * modal description for analytics, e.g. "Create sub-gallery"
     */
    analyticsValue?: string;
}

export function CreateCategoryModal(props: CreateCategoryModalProps) {
    const {
        open,
        onCancel,
        isChannel,
        title,
        creationUrl,
        privacy,
        switches,
        analyticsValue = `Create ${isChannel ? "Channel" : "Gallery"}`,
    } = props;

    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();

    const useFormHookMethods = useForm({
        mode: "onChange",
    });

    const sendButtonAnalytics = useButtonAnalytics();

    const handleCancel = () => {
        sendButtonAnalytics(
            `${analyticsValue} - cancel`,
            ButtonClickAnalyticsType.CLOSE,
            undefined,
            undefined,
            `Modal - ${analyticsValue}`
        );

        onCancel();
    };

    const handleSubmit = async (data: { [key: string]: string | boolean }) => {
        setProcessing(true);

        sendButtonAnalytics(
            `${analyticsValue} - create`,
            ButtonClickAnalyticsType.CREATE,
            undefined,
            undefined,
            `Modal - ${analyticsValue}`
        );

        try {
            const result = await postKmsData(creationUrl, data);
            if (result.success && result.redirectUrl) {
                // redirect to the new category page on success
                window.location.href = result.redirectUrl;
            }
            if (!result.success) {
                setProcessing(false);
                if (result.field && result.field === "name") {
                    // show name field error message, located below the input
                    useFormHookMethods.setError("name", { type: "manual", message: result.errorMessage });
                }
                else {
                    // show toast in case of other errors
                    showToast({ severity: "error", message: result.errorMessage });
                }
            }
        }
        catch (e) {
            setProcessing(false);
            showToast({
                severity: "error",
                message: isChannel ? translate("Failed to create channel") : translate("Failed to create gallery"),
            });
        }
    };

    const baseClassName = `kms-ds-create-${isChannel ? "channel" : "gallery"}-modal`;

    return (
        <ButtonAnalyticsProvider location={`Modal - ${analyticsValue}`}>
            <StyledCreateCategoryModal
                open={open}
                onClose={handleCancel}
                classes={{ paper: `${baseClassName}` }}
                onKeyDown={(ev) => ev.stopPropagation()}
            >
                <ModalTitle className={`${baseClassName}-title`}>{title}</ModalTitle>
                <ModalContent className={`${baseClassName}-content`}>
                    <FormProvider {...useFormHookMethods}>
                        {open && <CreateCategoryForm privacy={privacy} switches={switches} />}
                    </FormProvider>
                </ModalContent>
                <ModalActions className={`${baseClassName}-actions`}>
                    <Button
                        onClick={handleCancel}
                        variant={"borderless"}
                        color={"secondary"}
                        className={`${baseClassName}-actions-cancel`}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        onClick={useFormHookMethods.handleSubmit(handleSubmit)}
                        type={"submit"}
                        loading={processing}
                        disabled={processing || !useFormHookMethods.formState.isValid}
                        className={`${baseClassName}-actions-submit`}
                    >
                        {translate("Create")}
                    </Button>
                </ModalActions>
            </StyledCreateCategoryModal>
        </ButtonAnalyticsProvider>
    );
}

export default CreateCategoryModal;
